import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import { apiPatchAsUser, apiPost, apiPostAsUser } from '../../lib/api_v2';
import { BACKGROUND_IMAGE_CONTENT_TYPES } from '../../lib/backgroundImage';
import { enqueueUpload, removeMediaAttachment, waitForMediaAttachmentUpload } from '../../lib/media_attachments_store';
import { getMetaContent } from '../../lib/meta';
import db from '../../lib/db';
import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';
import { thumbhashToDataURL } from '../../lib/thumbdata';
import { beginPendingWidgetGenerationTurn, storeAgentChatWithGenerationTurn } from '../../lib/widgetGenerationTurns';
import { WORKSPACE_BACKGROUND_IMAGE_PRESETS } from '../../lib/workspaceBackgroundPresets';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import useBackgroundImageUpload from '../../hooks/useBackgroundImageUpload';
import useWorkspaceBackgroundImageMutation from '../../hooks/useWorkspaceBackgroundImageMutation';
import { useWorkspaceSetup, wallpaperImageUrl } from '../../hooks/useWorkspaceSetup';
import Flexicon from '../shared/Flexicon';
import PeoplePickerPanel from '../shared/PeoplePickerPanel';
import { BackgroundChoiceTile } from '../shared/WorkspaceBackgroundPicker';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_TITLE_CLASS } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import './spaceSetupAvatar.css';
import { ModalBody } from './ModalBody';
import { ModalTopBar } from './ModalTopBar';

const STEP = {
  PREPARING: 'preparing',
  NAME: 'name',
  MEMBERS: 'members',
  AVATAR: 'avatar',
  WALLPAPER: 'wallpaper',
  WIDGET: 'widget',
};

const STEP_TITLE = {
  [STEP.PREPARING]: 'Prepping your space',
  [STEP.NAME]: 'What do you want to name it?',
  [STEP.MEMBERS]: 'Add Members',
  [STEP.AVATAR]: 'Your space photo',
  [STEP.WALLPAPER]: 'Background Image',
  [STEP.WIDGET]: 'Create your first widget',
};

// Held long enough that the transition reads as a beat rather than a flicker, and
// released the moment the name exists. The ceiling is the safety valve: a slow or failed
// naming call must not strand the user on a screen with no way forward.
const PREPARING_MIN_MS = 600;
const PREPARING_MAX_MS = 8000;

// Roughly how long a cover photo takes end to end, measured against the real service.
// The ring is paced against this rather than against anything the API reports — image
// generation exposes no progress, only the refinement passes as they land.
const AVATAR_EXPECTED_MS = 12_000;
// The clock alone never fills the ring: it eases toward this and waits for the real
// event, so the ring cannot claim to be finished before the photo is.
const AVATAR_TIME_CEILING = 0.92;
// Floors the passes push it to, so a fast generation jumps ahead of the clock instead of
// crawling, and the ring only ever moves forwards.
const AVATAR_PASS_FLOORS = [0.55, 0.78];

const FLEXICON_IMAGE_CONTENT_TYPES = JSON.parse(getMetaContent('channel:flexicon_image_content_types') || '[]');
const WORKSPACE_FLEXICON_UPLOAD_PURPOSE = 'workspace_flexicon';

// The grid is 4 up; the upload tile always occupies the last cell of the first row.
const WALLPAPER_COLUMNS = 4;
// Which is why the opening batch is one short: it shares its row with the upload tile.
// Every "Generate more" row after it is full width. Mirrors WorkspaceSetup.wallpapers_in_batch.
const FIRST_BATCH_WALLPAPERS = WALLPAPER_COLUMNS - 1;

const TERMINAL_STATUSES = new Set(['succeeded', 'failed']);

// Stands in for a run that never started, so every step takes its failure path.
const FAILED_SETUP = {
  identity_status: 'failed',
  suggestions_status: 'failed',
  avatar_status: 'failed',
  avatar_preview: null,
  wallpaper_status: 'failed',
  generated_name: null,
  widget_suggestions: [],
  wallpapers: [],
  more_wallpapers_available: false,
};

// Every ingested attachment carries a thumbhash, so a tile can paint the real image's
// colors on the first frame instead of a grey box while the PNG downloads.
function attachmentPlaceholderUrl(attachment) {
  if (!attachment?.thumbhash) return null;

  try {
    return thumbhashToDataURL(attachment.thumbhash);
  } catch {
    return null;
  }
}

function PrimaryButton({ children, disabled = false, onClick }) {
  return (
    <button
      type="button"
      data-app-region="none"
      disabled={disabled}
      onClick={onClick}
      className="mt-6 flex h-12 w-full cursor-pointer items-center justify-center rounded-full bg-black px-4 text-[15px] leading-5 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-black"
    >
      {children}
    </button>
  );
}

// The title lives in the modal's own top bar (see the Modal below), so a step only
// renders its body. Steps stay vertically centered because the shared New Chat
// dimensions are much taller than any single step needs.
/**
 * A determinate-looking ring around the cover photo while it generates.
 *
 * Image generation reports no progress, so this is paced: it eases along a clock toward
 * a ceiling it cannot pass, and each refinement pass that lands shoves it to a floor
 * ahead of where the clock had it. Completion is the only thing that fills it. That
 * keeps it honest in the only way that matters — it never says finished before the photo
 * is, and it never runs backwards.
 */
function AvatarProgressRing({ progress }) {
  const radius = 47;
  const circumference = 2 * Math.PI * radius;

  return (
    <svg viewBox="0 0 100 100" aria-hidden="true" className="pointer-events-none absolute -inset-1.5 size-[calc(100%+0.75rem)] -rotate-90">
      <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="2.5" className="stroke-gray-200 dark:stroke-neutral-700" />
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - progress)}
        className="stroke-idle-blue transition-[stroke-dashoffset] duration-500 ease-out"
      />
    </svg>
  );
}

function StepShell({ children }) {
  return <div className="flex w-full flex-1 flex-col justify-center px-5 pb-6">{children}</div>;
}

/**
 * The beat between submitting the description and naming the space. It exists so the
 * name field is never shown empty and then filled in underneath the user — by the time
 * this clears, the generated name is there to be accepted or typed over.
 */
export function PreparingStep() {
  return (
    <StepShell>
      <div className="flex justify-center">
        <SpinnerIcon className="size-5 animate-spin text-gray-400" />
      </div>
    </StepShell>
  );
}

/** The name, prefilled with the generated suggestion and editable. Nothing here waits
 *  on an image, so it is usable about two seconds after the description is submitted. */
export function NameStep({ onConfirm, setup, workspaceId }) {
  const [name, setName] = useState('');
  const [hasEdited, setHasEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const generatedName = setup?.generated_name || '';
  const identityFailed = setup?.identity_status === 'failed';

  // Fill in when the name lands, but never overwrite what the user has typed.
  useEffect(
    function adoptGeneratedName() {
      if (hasEdited || !generatedName) return;
      setName(generatedName);
    },
    [generatedName, hasEdited],
  );

  const handleNameChange = useCallback(function handleNameChangeCallback(event) {
    setHasEdited(true);
    setName(event.target.value);
  }, []);

  const trimmedName = name.trim();

  const handleConfirm = useCallback(
    async function handleConfirmCallback() {
      if (!trimmedName || isSaving) {
        onConfirm();
        return;
      }

      setIsSaving(true);
      try {
        // Usually already correct — the identity job retitles the space as soon as the
        // generated name lands — so this only really fires when the user edited it.
        await apiPatchAsUser(`/workspaces/${workspaceId}`, { workspace: { title: trimmedName } });
      } catch (error) {
        logError('SpaceSetupFlow', 'Failed to save space name', { message: error?.message });
      } finally {
        setIsSaving(false);
        onConfirm();
      }
    },
    [isSaving, onConfirm, trimmedName, workspaceId],
  );

  return (
    <StepShell>
      <div className="mt-6 flex flex-col gap-2">
        <label htmlFor="space-setup-name" className="sr-only">
          Space name
        </label>
        <input
          id="space-setup-name"
          type="text"
          value={name}
          onChange={handleNameChange}
          placeholder={identityFailed || generatedName ? '' : 'Naming your space…'}
          autoFocus
          data-1p-ignore="true"
          className="h-12 w-full rounded-lg border border-white bg-gray-50 px-4 text-[15px] leading-5 text-black outline-none hover:bg-white focus:border-gray-400 sm:border-[0.5px] dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-100"
        />
      </div>

      <PrimaryButton disabled={!trimmedName || isSaving} onClick={handleConfirm}>
        Looks good
      </PrimaryButton>
    </StepShell>
  );
}

/**
 * Adding people, sitting between the name and the photo. Reuses the picker from the
 * Add Members sheet so search, suggestions and selection behave identically — minus the
 * invite link, which is `leadingContent` there and simply omitted here.
 *
 * It also earns its place in the flow: picking people takes real time, and the cover
 * photo and wallpapers finish generating behind it.
 */
export function MembersStep({ onConfirm, workspaceId }) {
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleUser = useCallback(function toggleUser(user) {
    setSelectedUserIds((current) => {
      const next = new Set(current);
      if (next.has(user.id)) next.delete(user.id);
      else next.add(user.id);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(
    async function confirmMembers() {
      if (isSubmitting) return;
      if (selectedUserIds.size === 0) {
        onConfirm();
        return;
      }

      setIsSubmitting(true);
      try {
        await apiPostAsUser(`/workspaces/${workspaceId}/members`, { members: { user_ids: Array.from(selectedUserIds) } });
      } catch (error) {
        // Not worth stranding the user mid-flow; they can add people from the space.
        logError('SpaceSetupFlow', 'Failed to add members', { message: error?.message });
      } finally {
        setIsSubmitting(false);
        onConfirm();
      }
    },
    [isSubmitting, onConfirm, selectedUserIds, workspaceId],
  );

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden">
        <PeoplePickerPanel
          workspaceId={workspaceId}
          selectionMode="multiple"
          selectedUserIds={selectedUserIds}
          onToggleUser={handleToggleUser}
          emptySuggestedMessage="Nobody to suggest yet — you can add people any time from the space."
        />
      </div>
      <div className="shrink-0 px-5 pb-6">
        <PrimaryButton disabled={isSubmitting} onClick={handleConfirm}>
          {selectedUserIds.size > 0 ? `Add ${selectedUserIds.size}` : 'Skip for now'}
        </PrimaryButton>
      </div>
    </div>
  );
}

/** The generated cover photo, resolving in place. Its own step so the name is never held
 *  up by it, and so the image has somewhere to be watched rather than sitting next to a
 *  form field the user is trying to fill in. */
export function AvatarStep({ onConfirm, setup, workspace, workspaceId }) {
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const hasGeneratedAvatar = workspace?.flexicon?.kind === 'image';
  const avatarFailed = setup?.avatar_status === 'failed';
  const avatarGenerating = Boolean(setup) && (setup.avatar_status === 'pending' || setup.avatar_status === 'generating');
  const avatarPreview = setup?.avatar_preview || null;

  // How many distinct refinement passes have landed. Tracked as state rather than a ref
  // because both the blur and the ring render from it, and a ref mutated during render
  // would not re-render either.
  const [passCount, setPassCount] = useState(0);
  const seenPassesRef = useRef(new Set());
  useEffect(
    function countPasses() {
      if (!avatarPreview || seenPassesRef.current.has(avatarPreview)) return;

      seenPassesRef.current.add(avatarPreview);
      setPassCount(seenPassesRef.current.size);
    },
    [avatarPreview],
  );

  const previewUrl = useMemo(() => {
    if (!avatarPreview) return null;

    try {
      return thumbhashToDataURL(avatarPreview);
    } catch {
      return null;
    }
  }, [avatarPreview]);

  // Each refinement pass drops the overall softness a step, so the sweep travels over a
  // frame that is itself getting sharper.
  const softBlur = Math.max(3, 14 - Math.max(0, passCount - 1) * 5);

  const [clockProgress, setClockProgress] = useState(0);
  const startedAtRef = useRef(Date.now());

  useEffect(
    function advanceTheClock() {
      if (hasGeneratedAvatar || avatarFailed) return undefined;

      const timer = setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current;
        // Ease out, so it moves briskly at first and then slows as it approaches the
        // ceiling rather than stalling dead at it.
        const eased = 1 - Math.pow(1 - Math.min(1, elapsed / AVATAR_EXPECTED_MS), 2);
        setClockProgress(eased * AVATAR_TIME_CEILING);
      }, 120);

      return () => clearInterval(timer);
    },
    [avatarFailed, hasGeneratedAvatar],
  );

  const progress = useMemo(() => {
    if (hasGeneratedAvatar) return 1;
    if (avatarFailed) return 0;

    const passFloor = AVATAR_PASS_FLOORS[passCount - 1] || 0;
    return Math.max(clockProgress, passFloor);
  }, [avatarFailed, clockProgress, hasGeneratedAvatar, passCount]);

  // An upload replaces the generated photo and wins permanently — GenerateAvatar bails
  // when the workspace already has an image flexicon, so a late arrival cannot overwrite it.
  const handleAvatarFile = useCallback(
    async function handleAvatarFileCallback(file) {
      if (!file || isUploadingAvatar) return;

      setIsUploadingAvatar(true);
      let uploadId = null;
      try {
        uploadId = await enqueueUpload({
          file,
          purpose: WORKSPACE_FLEXICON_UPLOAD_PURPOSE,
          workspaceId,
          sessionKey: `space-setup:${workspaceId}:flexicon`,
        });
        const attachment = await waitForMediaAttachmentUpload(uploadId);
        await apiPatchAsUser(`/workspaces/${workspaceId}`, {
          workspace: { flexicon_type: 'image', flexicon_id: attachment.attachment_id },
        });
      } catch (error) {
        logError('SpaceSetupFlow', 'Failed to upload space avatar', { message: error?.message });
        if (uploadId) await removeMediaAttachment(uploadId).catch(() => {});
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    [isUploadingAvatar, workspaceId],
  );

  const avatarDropzoneAccept = useMemo(() => Object.fromEntries(FLEXICON_IMAGE_CONTENT_TYPES.map((type) => [type, []])), []);
  const { getInputProps: getAvatarInputProps, open: openAvatarPicker } = useDropzone({
    accept: avatarDropzoneAccept,
    disabled: isUploadingAvatar,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted) => handleAvatarFile(accepted[0]),
  });

  return (
    <StepShell>
      <div className="mt-6 flex justify-center">
        <div className="relative size-28">
          <input {...getAvatarInputProps()} />
          {/* `relative` matters: the sweep, the settled photo and the spinner are all
              absolutely positioned, and without a positioned ancestor here they resolve
              against the outer wrapper and spill outside the circle. */}
          <div className="relative size-28 overflow-hidden rounded-full bg-gray-100 dark:bg-neutral-800">
            {/* The image arrives as refinement passes, each a thumbhash of the frame so
                far: it paints blurred and sharpens with every pass, then the real photo
                takes over. The picture is the thing on screen throughout. */}
            {previewUrl && !hasGeneratedAvatar ? (
              <>
                <img
                  src={previewUrl}
                  alt=""
                  draggable="false"
                  className="size-full scale-110 object-cover transition-[filter] duration-700 ease-out"
                  style={{ filter: `blur(${softBlur}px)` }}
                />
                {/* The same frame, sharper, revealed by a band travelling down it. */}
                <img
                  src={previewUrl}
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                  className="space-setup-avatar-sweep absolute inset-0 size-full scale-110 object-cover transition-[filter] duration-700 ease-out"
                  style={{ filter: `blur(${Math.max(1, softBlur - 6)}px)` }}
                />
              </>
            ) : null}
            {hasGeneratedAvatar ? (
              <div className="space-setup-avatar-settle absolute inset-0">
                <Flexicon resource={workspace} size="size-28" />
              </div>
            ) : null}
          </div>
          {avatarGenerating && !hasGeneratedAvatar ? <AvatarProgressRing progress={progress} /> : null}
          <button
            type="button"
            data-app-region="none"
            aria-label="Upload your own space photo"
            disabled={isUploadingAvatar}
            onClick={openAvatarPicker}
            className="absolute right-0 bottom-0 flex size-8 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-white bg-gray-100 text-gray-500 shadow-[0px_2px_6px_0px_rgba(0,0,0,0.12)] hover:bg-white hover:text-black disabled:cursor-wait disabled:opacity-60 dark:border-white/10 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-100"
          >
            {isUploadingAvatar ? <SpinnerIcon className="size-3.5 animate-spin" /> : <Upload size={14} strokeWidth={1.5} absoluteStrokeWidth />}
          </button>
        </div>
      </div>

      <PrimaryButton onClick={onConfirm}>Looks good</PrimaryButton>
    </StepShell>
  );
}

/** Screens D and E — generated wallpaper candidates; picking one applies it live. */
export function WallpaperStep({ generateMoreWallpapers, onConfirm, setup, workspace, workspaceId }) {
  const [busyAttachmentId, setBusyAttachmentId] = useState(null);
  const { saveAttachment, savePreset } = useWorkspaceBackgroundImageMutation(workspaceId);

  const wallpapers = useMemo(() => setup?.wallpapers || [], [setup?.wallpapers]);
  const isGenerating = !setup || setup.wallpaper_status === 'pending' || setup.wallpaper_status === 'generating';
  const generationFailed = setup?.wallpaper_status === 'failed';
  const selectedId = workspace?.background_image?.id ? String(workspace.background_image.id) : null;

  const handleSelect = useCallback(
    async function handleSelectCallback(attachment) {
      const attachmentId = String(attachment.id);
      setBusyAttachmentId(attachmentId);
      try {
        await saveAttachment(attachmentId);
      } catch (error) {
        logError('SpaceSetupFlow', 'Failed to apply wallpaper', { message: error?.message });
      } finally {
        setBusyAttachmentId(null);
      }
    },
    [saveAttachment],
  );

  const handleUploadSaved = useCallback(
    async function handleUploadSavedCallback(attachmentId) {
      try {
        await saveAttachment(String(attachmentId));
      } catch (error) {
        logError('SpaceSetupFlow', 'Failed to apply uploaded wallpaper', { message: error?.message });
      }
    },
    [saveAttachment],
  );

  const {
    isBusy: isUploadBusy,
    rejectFile,
    uploadFile,
  } = useBackgroundImageUpload({
    backgroundImage: workspace?.background_image || null,
    backgroundImageUrl: workspace?.background_image_url || null,
    uploadWorkspaceId: workspaceId,
    sessionKeyScope: 'space-setup-background',
    externallyDisabled: !workspaceId,
    contentTypes: BACKGROUND_IMAGE_CONTENT_TYPES,
    onSave: handleUploadSaved,
  });

  const handleDrop = useCallback(
    function handleDropCallback(acceptedFiles) {
      uploadFile(acceptedFiles[0]);
    },
    [uploadFile],
  );
  const dropzoneAccept = useMemo(() => Object.fromEntries(BACKGROUND_IMAGE_CONTENT_TYPES.map((type) => [type, []])), []);
  const { getInputProps, open: openFilePicker } = useDropzone({
    accept: dropzoneAccept,
    disabled: isUploadBusy,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    onDropRejected: rejectFile,
  });

  // Nothing generated and nothing coming: fall back to the static presets so the step
  // is still useful rather than an empty grid.
  const presetKeys = wallpapers.length === 0 && generationFailed ? Object.keys(WORKSPACE_BACKGROUND_IMAGE_PRESETS) : [];

  const choiceTiles = [
    ...wallpapers.map((attachment) => (
      <BackgroundChoiceTile
        key={attachment.id}
        accessibleLabel="Use this background"
        busy={busyAttachmentId === String(attachment.id)}
        disabled={Boolean(busyAttachmentId)}
        className="h-auto aspect-[3/2]"
        imageUrl={wallpaperImageUrl(attachment)}
        placeholderUrl={attachmentPlaceholderUrl(attachment)}
        onClick={() => handleSelect(attachment)}
        selected={selectedId === String(attachment.id)}
      />
    )),
    ...presetKeys.map((preset) => (
      <BackgroundChoiceTile
        key={preset}
        accessibleLabel={`Use the ${WORKSPACE_BACKGROUND_IMAGE_PRESETS[preset]?.label || preset} background`}
        className="h-auto aspect-[3/2]"
        disabled={Boolean(busyAttachmentId)}
        imageUrl={WORKSPACE_BACKGROUND_IMAGE_PRESETS[preset]?.url}
        onClick={() => savePreset(preset)}
        selected={workspace?.background_image_preset === preset}
      />
    )),
    ...(isGenerating
      ? Array.from({ length: wallpapers.length === 0 ? FIRST_BATCH_WALLPAPERS : WALLPAPER_COLUMNS }, (_, index) => (
          <div
            key={`wallpaper-skeleton-${index}`}
            className="flex aspect-[3/2] w-full animate-pulse items-center justify-center rounded-lg border-[0.5px] border-white bg-gray-100 dark:border-white/10 dark:bg-neutral-800"
          >
            <SpinnerIcon className="size-4 animate-spin text-gray-400" />
          </div>
        ))
      : []),
  ];

  const uploadTile = (
    <BackgroundChoiceTile
      key="upload"
      accessibleLabel="Upload your own background"
      className="h-auto aspect-[3/2]"
      busy={isUploadBusy}
      disabled={isUploadBusy}
      onClick={openFilePicker}
      selected={false}
    >
      <Upload size={18} strokeWidth={1.5} absoluteStrokeWidth />
    </BackgroundChoiceTile>
  );

  return (
    <StepShell>
      <div className="mt-5 grid w-full grid-cols-4 gap-2">
        <input {...getInputProps()} />
        {/* Upload is pinned to the end of the first row rather than flowed after the
            candidates, so it stays in the same place as batches arrive and "Generate
            more" pushes the grid onto further rows. */}
        {[...choiceTiles.slice(0, WALLPAPER_COLUMNS - 1), uploadTile, ...choiceTiles.slice(WALLPAPER_COLUMNS - 1)]}
      </div>

      {setup?.more_wallpapers_available ? (
        <button
          type="button"
          data-app-region="none"
          disabled={isGenerating}
          onClick={generateMoreWallpapers}
          className="mt-4 self-center text-[13px] leading-5 text-gray-500 underline underline-offset-2 hover:text-black disabled:cursor-not-allowed disabled:opacity-40 dark:text-neutral-400 dark:hover:text-neutral-100"
        >
          Generate more
        </button>
      ) : null}

      <PrimaryButton onClick={onConfirm}>Looks good</PrimaryButton>
    </StepShell>
  );
}

/** Screen F — three generated widget briefs, plus an escape hatch and a skip. */
export function WidgetStep({ onOpenWidgetComposer, onPicked, onSkip, setup, workspaceId }) {
  const [busyIndex, setBusyIndex] = useState(null);

  const suggestions = useMemo(() => setup?.widget_suggestions || [], [setup?.widget_suggestions]);
  const isGenerating = !setup || setup.suggestions_status === 'pending' || setup.suggestions_status === 'generating';

  // The card's hidden brief lives server-side; ask for it only when the user picks that
  // card, then hand it to the widget agent exactly as WidgetStudio does.
  //
  // The modal closes first and the request runs behind it. Awaiting the round trip left
  // the user staring at a spinning card for as long as it took the agent chat to be
  // created and its first turn queued — a wait that bought them nothing, since the
  // status banner in the space is where the build is actually watched.
  const handlePick = useCallback(
    function handlePickCallback(index) {
      if (busyIndex !== null) return;
      setBusyIndex(index);
      onPicked();

      apiPost(`/workspaces/${workspaceId}/setup/widget`, { workspace_setup: { suggestion_index: index } })
        .then(async function storeStartedChat(response) {
          const data = await response.json();
          const agentChat = data?.data?.agent_chat;
          if (!(response.ok && data?.ok && agentChat?.id)) throw new Error('widget_handoff_failed');

          // Caching the chat is not enough to raise the build-status banner — that is
          // driven by a pending turn keyed on the request message, which is why the
          // endpoint hands one back.
          await storeAgentChatWithGenerationTurn(agentChat);
          beginPendingWidgetGenerationTurn({
            workspaceId: String(workspaceId),
            agentChatId: String(agentChat.id),
            requestMessageId: data?.data?.agent_chat_message?.id,
            startedAt: Math.floor(Date.now() / 1000),
          });
        })
        .catch(function reportFailure(error) {
          logError('SpaceSetupFlow', 'Failed to start widget from suggestion', { message: error?.message });
        });
    },
    [busyIndex, onPicked, workspaceId],
  );

  const handleOwnPrompt = useCallback(
    function handleOwnPromptCallback() {
      onOpenWidgetComposer({ workspaceId });
    },
    [onOpenWidgetComposer, workspaceId],
  );

  return (
    <StepShell>
      <div className="mt-5 flex w-full flex-col gap-2">
        {isGenerating
          ? Array.from({ length: 3 }, (_, index) => (
              <div
                key={`suggestion-skeleton-${index}`}
                className="h-19 w-full animate-pulse rounded-lg border-[0.5px] border-white bg-gray-100 dark:border-white/10 dark:bg-neutral-800"
              />
            ))
          : suggestions.map((suggestion, index) => (
              <button
                key={suggestion.title}
                type="button"
                data-app-region="none"
                disabled={busyIndex !== null}
                onClick={() => handlePick(index)}
                className={cn(
                  'relative flex w-full cursor-pointer flex-col gap-1 rounded-lg border-[0.5px] border-white bg-gray-50 px-4 py-3 text-left transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-neutral-900',
                )}
              >
                <span className="text-[15px] leading-5 font-medium text-black dark:text-neutral-100">{suggestion.title}</span>
                <span className="text-[13px] leading-5 text-gray-500 dark:text-neutral-400">{suggestion.pitch}</span>
                {busyIndex === index ? (
                  <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60 dark:bg-black/50">
                    <SpinnerIcon className="size-4 animate-spin text-gray-500" />
                  </span>
                ) : null}
              </button>
            ))}

        <button
          type="button"
          data-app-region="none"
          disabled={busyIndex !== null}
          onClick={handleOwnPrompt}
          className="flex w-full cursor-pointer flex-col gap-1 rounded-lg border-[0.5px] border-white bg-gray-50 px-4 py-3 text-left transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-neutral-900"
        >
          <span className="text-[15px] leading-5 font-medium text-black dark:text-neutral-100">My own prompt</span>
          <span className="text-[13px] leading-5 text-gray-500 dark:text-neutral-400">I already have something in mind</span>
        </button>
      </div>

      <button
        type="button"
        data-app-region="none"
        onClick={onSkip}
        className="mt-6 flex h-12 w-full cursor-pointer items-center justify-center rounded-full bg-gray-400 px-4 text-[15px] leading-5 font-medium text-white hover:opacity-90 dark:bg-neutral-700"
      >
        Skip
      </button>
    </StepShell>
  );
}

/**
 * Screens C through F of the AI space flow, in one small modal so the space itself stays
 * visible behind it — applying a wallpaper repaints the chat live underneath.
 *
 * Closing at any step is safe: the generation jobs finish regardless, and anything
 * already applied persists. Only the unused widget suggestions are lost.
 */
const SpaceSetupFlow = ({ onAbandon, onClose, onDismissibleChange, onOpenWidgetComposer, workspaceId }) => {
  const [step, setStep] = useState(STEP.PREPARING);
  const mountedAtRef = useRef(Date.now());
  const { setup: polledSetup, isMissing, hasTimedOut, generateMoreWallpapers } = useWorkspaceSetup(workspaceId);

  // No run to watch (the setup request never landed) or one that stopped answering:
  // every step reads its own status, so present it as failed and let each degrade to
  // its manual affordance rather than spinning on a generation that is not coming.
  const setup = useMemo(() => {
    if (isMissing) return FAILED_SETUP;
    if (!polledSetup) return null;
    if (!hasTimedOut) return polledSetup;

    return {
      ...polledSetup,
      identity_status: TERMINAL_STATUSES.has(polledSetup.identity_status) ? polledSetup.identity_status : 'failed',
      suggestions_status: TERMINAL_STATUSES.has(polledSetup.suggestions_status) ? polledSetup.suggestions_status : 'failed',
      avatar_status: TERMINAL_STATUSES.has(polledSetup.avatar_status) ? polledSetup.avatar_status : 'failed',
      wallpaper_status: TERMINAL_STATUSES.has(polledSetup.wallpaper_status) ? polledSetup.wallpaper_status : 'failed',
    };
  }, [hasTimedOut, isMissing, polledSetup]);

  const workspace = useLiveQuery(
    function loadWorkspace() {
      if (!workspaceId) return undefined;
      return db.workspaces.get(String(workspaceId));
    },
    [workspaceId],
  );

  // Leave the preparing beat as soon as there is a name to show — or once the naming
  // call has failed or run long, so the flow always moves on.
  const nameReady = Boolean(setup?.generated_name) || setup?.identity_status === 'failed';
  useEffect(
    function leavePreparingWhenNamed() {
      if (step !== STEP.PREPARING) return undefined;

      const elapsed = Date.now() - mountedAtRef.current;
      if (nameReady) {
        const remaining = Math.max(0, PREPARING_MIN_MS - elapsed);
        const timer = setTimeout(() => setStep(STEP.NAME), remaining);
        return () => clearTimeout(timer);
      }

      const timer = setTimeout(() => setStep(STEP.NAME), Math.max(0, PREPARING_MAX_MS - elapsed));
      return () => clearTimeout(timer);
    },
    [nameReady, step],
  );

  // The space is provisional until it is named: it exists server-side only so generation
  // has somewhere to hang, and it is still sitting under a placeholder title. Backing out
  // here therefore discards it rather than leaving an unfinished space in the sidebar
  // with no route back into setup. After naming, closing is an ordinary dismissal.
  const isProvisional = step === STEP.PREPARING || step === STEP.NAME;
  useEffect(
    function reportDismissible() {
      // Both paths close the modal; the host only needs to know which one to run.
      onDismissibleChange?.(true);
    },
    [onDismissibleChange],
  );

  const handleDismiss = useCallback(
    function dismissFlow() {
      if (isProvisional) onAbandon?.();
      else onClose();
    },
    [isProvisional, onAbandon, onClose],
  );

  const handleNameConfirmed = useCallback(function nameConfirmed() {
    setStep(STEP.MEMBERS);
  }, []);

  const handleMembersConfirmed = useCallback(function membersConfirmed() {
    setStep(STEP.AVATAR);
  }, []);

  const handleAvatarConfirmed = useCallback(function avatarConfirmed() {
    setStep(STEP.WALLPAPER);
  }, []);

  const handleWallpaperConfirmed = useCallback(
    function wallpaperConfirmed() {
      // With no plan there are no suggestions to show; the widget step would be an
      // "own prompt" card alone, which is just a worse version of closing.
      if (setup?.suggestions_status === 'failed') {
        onClose();
        return;
      }
      setStep(STEP.WIDGET);
    },
    [onClose, setup?.suggestions_status],
  );

  const handleOpenComposer = useCallback(
    function openComposer(target) {
      onClose();
      onOpenWidgetComposer(target);
    },
    [onClose, onOpenWidgetComposer],
  );

  return (
    <Modal
      onClose={handleDismiss}
      className="rounded-2xl"
      closePlacement="top-left"
      showClose={{ desktop: true, mobile: true }}
      padding="none"
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      <ModalBody padding="none" className="flex h-full flex-col overflow-hidden">
        <div className="shrink-0 pt-5">
          <ModalTopBar className="!pb-0">
            <span className={SETTINGS_MODAL_TITLE_CLASS}>{STEP_TITLE[step]}</span>
          </ModalTopBar>
        </div>
        {step === STEP.PREPARING ? <PreparingStep /> : null}
        {step === STEP.NAME ? (
          <NameStep onConfirm={handleNameConfirmed} setup={setup} workspaceId={workspaceId} />
        ) : null}
        {step === STEP.MEMBERS ? <MembersStep onConfirm={handleMembersConfirmed} workspaceId={workspaceId} /> : null}
        {step === STEP.AVATAR ? (
          <AvatarStep onConfirm={handleAvatarConfirmed} setup={setup} workspace={workspace} workspaceId={workspaceId} />
        ) : null}
        {step === STEP.WALLPAPER ? (
          <WallpaperStep
            generateMoreWallpapers={generateMoreWallpapers}
            onConfirm={handleWallpaperConfirmed}
            setup={setup}
            workspace={workspace}
            workspaceId={workspaceId}
          />
        ) : null}
        {step === STEP.WIDGET ? (
          <WidgetStep
            onOpenWidgetComposer={handleOpenComposer}
            onPicked={onClose}
            onSkip={onClose}
            setup={setup}
            workspaceId={workspaceId}
          />
        ) : null}
      </ModalBody>
    </Modal>
  );
};

export default SpaceSetupFlow;
